/**
 * CreateAccountPage Component
 *
 * Container component for the user registration flow.
 * Manages validation of form inputs, user registration via Supabase,
 * and displays appropriate success/error feedback.
 *
 * Key features:
 * - Email and password validation
 * - Referral tracking from URL parameter
 * - "How did you hear about us" form handling
 * - Google Analytics integration via gclid tracking
 * - Real-time password strength validation
 * @returns {JSX.Element} Registration form with validation feedback
 */
import CreateAccountForm from "./create-account-form";
import React, { useEffect, useState } from "react";
import validator from "validator";
import { useParams, useLocation } from "react-router";
import isStrongPassword from "validator/lib/isStrongPassword";
import isLength from "validator/lib/isLength";
import { Button, Alert, Spinner } from "react-bootstrap";
import { IoMdClose } from "react-icons/io";
import { BiAnalyse } from "react-icons/bi";
import { AiOutlineCloseCircle, AiOutlineCheckCircle } from "react-icons/ai";
import axios from "axios";
import { hearAboutUsFieldConfig } from "../forms/configs";
import { useSupabaseApp } from "../../SupabaseApp";
import { extractGclid } from "../../utils/helpers";

function CreateAccountPage() {
  // Get Supabase client and registration function from context
  const { supabase, register } = useSupabaseApp();

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const next = searchParams.get("next");

  // Get referral slug from URL parameters (e.g., /create-account/partner-name)
  const { slug } = useParams();

  // Initial form state with all required fields for user creation
  const initialValues = {
    firstname: "",
    lastname: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",

    referredBy: slug,

    knowYouBetter: "",
    hearAboutUs: "",
    id: -1,
    event: "create account",
    // hearAboutUs Fields
    emailDescription: "",
    emailDate: "",
    emailUrl: "",
    socialMediaPlatform: "",
    socialMediaDescription: "",
    socialMediaPaid: "",
    socialMediaDate: "",
    socialMediaUrl: "",
    searchEnginePlatform: "",
    searchEngineOther: "",
    searchPaid: "",
    searchDescription: "",
    searchDate: "",
    searchUrl: "",
    adPlatform: "",
    adDescription: "",
    adDate: "",
    adUrl: "",
    wordOfMouthDescription: "",
    wordOfMouthDate: "",
    wordOfMouthFrom: "",
    govDescription: "",
    govReceivedMethod: "",
    govDate: "",
    govUrl: "",
    recommendWhoName: "",
    recommendHow: "",
    recommendDate: "",
    recommendContact: "",
    researchPlatform: "",
    researchDescription: "",
    researchDate: "",
    researchUrl: "",
    newsDescription: "",
    newsDate: "",
    newsUrl: "",
    eventLocation: "",
    eventDescription: "",
    eventDate: "",
    eventUrl: "",
    schoolName: "",
    schoolDescription: "",
    schoolDate: "",
    schoolUrl: "",
    podcastPlatform: "",
    podcastDescription: "",
    podcastDate: "",
    podcastUrl: "",
    otherInfo: "",
    otherUrl: "",
  };

  // User form data state
  const [newUser, setNewUser] = useState(initialValues);

  // Alert/response message state
  const [response, setResponse] = useState("");

  // CAPTCHA token state for bot prevention
  const [captchaToken, setCaptchaToken] = useState("");

  /**
   * Handle user registration submission
   * Validates all form fields, passwords, and email before submitting to Supabase
   * @param {Object} user - User object with all form data
   */
  async function handleRegistration(user) {
    // Validate email format
    const isValidEmailAddress = validateEmailAddress(user.email);
    // Check if password and confirmation match
    const passwordsMatch =
      user.password.localeCompare(user.confirmPassword) === 0;

    // List of required fields that must be filled
    const requiredFields = [
      "knowYouBetter",
      "hearAboutUs",
      "firstname",
      "lastname",
      "email",
      "password",
      "confirmPassword",
      "username",
    ];

    // Check if all basic required fields are populated
    const areBasicFieldsFilled = requiredFields.every(
      (field) =>
        user[field] !== "" && user[field] !== undefined && user[field] !== null,
    );

    // Check if fields specific to selected "How did you hear about us" option are filled
    const areHearAboutUsFieldsFilled = !hearAboutUsFieldConfig[
      user.hearAboutUs
    ]?.some((field) => {
      if (!field.required) return false;
      const value = user[field.name];
      return value === "" || value === undefined || value === null;
    });

    // Combine all validation checks
    const fieldsCheck = areBasicFieldsFilled && areHearAboutUsFieldsFilled;

    // Proceed with registration if all validations pass
    if (fieldsCheck) {
      if (isValidEmailAddress) {
        if (passwordsMatch) {
          // Show loading alert to user
          setResponse(
            <Alert className="error-message floating-alert" variant="light">
              <div>
                <div style={{ textAlign: "right" }}>
                  <Button
                    className="pitchdeckmessageclose"
                    onClick={(event) => {
                      event.preventDefault();
                      setResponse("");
                    }}
                  >
                    <IoMdClose />
                  </Button>
                </div>
                <div style={{ display: "flex", paddingTop: "10px" }}>
                  <div style={{ width: "90%" }}>
                    <p>
                      <span style={{ color: "#0f9d58" }}>
                        <BiAnalyse className="alert-icon" /> Loading
                      </span>
                    </p>
                    <p style={{ marginLeft: "28px", fontSize: "14px" }}>
                      Attempting to log in
                    </p>
                    <p style={{ marginLeft: "28px", fontSize: "14px" }}>
                      If your waiting time is too long,{" "}
                      <a
                        href="https://www.roiquant.com/contact"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        contact us
                      </a>
                      .
                    </p>
                  </div>
                  <div style={{ width: "10%", textAlign: "right" }}>
                    <Spinner animation="border" variant="secondary" />
                  </div>
                </div>
              </div>
            </Alert>,
          );

          try {
            // console.log("Placeholder message: New account created");
            // Extract Google Analytics click ID from URL for tracking
            const gclid = extractGclid();
            // Prepare submission data with trimmed names and captcha token
            const submissionData = {
              ...user,
              firstname: trimName(user.firstname),
              lastname: trimName(user.lastname),
              username: trimName(user.username),
              captchaToken: captchaToken,
              gclid: gclid,
              referredBy: user.referredBy || slug || "", // fallback to ensure slug is provided
              next,
              appUrl: window.location.origin,
            };
            const { error } = await supabase.functions.invoke("registration", {
              body: submissionData,
            });
            if (error) throw new Error((await error.context.json()).error);
          } catch (error) {
            const errMsg = error?.data
              ? JSON.stringify(error.data)
              : (error?.message ?? String(error));

            if (errMsg.includes("already been registered")) {
              setResponse(
                <Alert className="error-message floating-alert" variant="light">
                  <div style={{ textAlign: "right" }}>
                    <Button
                      className="pitchdeckmessageclose"
                      onClick={(event) => {
                        event.preventDefault();
                        setResponse("");
                      }}
                    >
                      <IoMdClose />
                    </Button>
                  </div>
                  <div style={{ paddingTop: "10px" }}>
                    <p style={{ color: "red" }}>
                      <AiOutlineCloseCircle className="alert-icon" /> Error
                    </p>
                    <p style={{ marginLeft: "28px" }}>
                      The email you've entered is already in use. Did you forget
                      your password? <br></br>
                      <br></br>No worries, we've got you.{" "}
                      <a href="https://app.roiquant.com/reset-password">
                        Reset your password now
                      </a>{" "}
                      to log in.
                    </p>
                  </div>
                </Alert>,
              );
            } else {
              setResponse(
                <Alert className="error-message floating-alert" variant="light">
                  <div style={{ textAlign: "right" }}>
                    <Button
                      className="pitchdeckmessageclose"
                      onClick={(event) => {
                        event.preventDefault();
                        setResponse("");
                      }}
                    >
                      <IoMdClose />
                    </Button>
                  </div>
                  <div style={{ paddingTop: "10px" }}>
                    <p style={{ color: "red" }}>
                      <AiOutlineCloseCircle className="alert-icon" /> Error
                    </p>
                    <p style={{ marginLeft: "28px" }}>{errMsg}</p>
                  </div>
                </Alert>,
              );
            }

            return;
          }

          setResponse(
            <Alert className="error-message floating-alert" variant="light">
              <div style={{ textAlign: "right" }}>
                <Button
                  className="pitchdeckmessageclose"
                  onClick={(event) => {
                    event.preventDefault();
                    setResponse("");
                  }}
                >
                  <IoMdClose />
                </Button>
              </div>
              <div style={{ paddingTop: "10px" }}>
                <p style={{ color: "#0f9d58" }}>
                  <AiOutlineCheckCircle className="alert-icon" /> Successful
                </p>
                <p style={{ marginLeft: "28px" }}>
                  Your account was successfully created! We've sent you an email
                  to verify your account.
                </p>
                <p style={{ marginLeft: "28px" }}>
                  For security reasons, you need to verify your account in order
                  to use our services.
                </p>
              </div>
            </Alert>,
          );
        } else {
          setResponse(
            <Alert className="error-message floating-alert" variant="light">
              <div style={{ textAlign: "right" }}>
                <Button
                  className="pitchdeckmessageclose"
                  onClick={(event) => {
                    event.preventDefault();
                    setResponse("");
                  }}
                >
                  <IoMdClose />
                </Button>
              </div>
              <div style={{ paddingTop: "10px" }}>
                <p style={{ color: "red" }}>
                  <AiOutlineCloseCircle className="alert-icon" /> Error
                </p>
                <p style={{ marginLeft: "28px" }}>Passwords do not match</p>
              </div>
            </Alert>,
          );
        }
      } else {
        setResponse(
          <Alert className="error-message floating-alert" variant="light">
            <div style={{ textAlign: "right" }}>
              <Button
                className="pitchdeckmessageclose"
                onClick={(event) => {
                  event.preventDefault();
                  setResponse("");
                }}
              >
                <IoMdClose />
              </Button>
            </div>
            <div style={{ paddingTop: "10px" }}>
              <p style={{ color: "red" }}>
                <AiOutlineCloseCircle className="alert-icon" /> Error
              </p>
              <p style={{ marginLeft: "28px" }}>
                You have entered an invalid email address. Please double check
                and enter a valid email address.
              </p>
            </div>
          </Alert>,
        );
      }
    } else {
      setResponse(
        <Alert className="error-message floating-alert" variant="light">
          <div style={{ textAlign: "right" }}>
            <Button
              className="pitchdeckmessageclose"
              onClick={(event) => {
                event.preventDefault();
                setResponse("");
              }}
            >
              <IoMdClose />
            </Button>
          </div>
          <div style={{ paddingTop: "10px" }}>
            <p style={{ color: "red" }}>
              <AiOutlineCloseCircle className="alert-icon" /> Error
            </p>
            <p style={{ marginLeft: "28px" }}>
              Please fill in the empty fields.
            </p>
          </div>
        </Alert>,
      );
    }
  }

  /**
   * Email validation utility
   * @param {string} emailAddress - Email to validate
   * @returns {boolean} True if email is valid format
   */
  function validateEmailAddress(emailAddress) {
    return validator.isEmail(emailAddress);
  }

  /**
   * Trim whitespace from name fields
   * @param {string} name - Name string to trim
   * @returns {string} Trimmed name
   */
  function trimName(name) {
    return validator.trim(name);
  }

  /**
   * Password validation utility functions
   * Each function returns a boolean indicating if the password meets the requirement
   */

  // Check if new password and confirm password fields match
  function checkPwdMatch(password, confirmPassword) {
    return password.localeCompare(confirmPassword) === 0;
  }

  // Check if password has minimum 8 characters
  function checkPwdLength(password) {
    return isLength(password, { min: 8 });
  }

  // Get current password length for display
  function measurePwdLength(password) {
    return password.length;
  }

  // Check if password contains at least one special symbol
  function checkPwdSymbol(password) {
    return isStrongPassword(password, {
      minUppercase: 0,
      minLength: 0,
      minNumbers: 0,
      minSymbols: 1,
      minLowercase: 0,
    });
  }

  // Check if password contains at least one uppercase letter
  function checkPwdUppercase(password) {
    return isStrongPassword(password, {
      minUppercase: 1,
      minLength: 0,
      minNumbers: 0,
      minSymbols: 0,
      minLowercase: 0,
    });
  }

  // Check if password contains at least one lowercase letter
  function checkPwdLowercase(password) {
    return isStrongPassword(password, {
      minUppercase: 0,
      minLength: 0,
      minNumbers: 0,
      minSymbols: 0,
      minLowercase: 1,
    });
  }

  // Check if password contains at least one number
  function checkPwdNum(password) {
    return isStrongPassword(password, {
      minUppercase: 0,
      minLength: 0,
      minNumbers: 1,
      minSymbols: 0,
      minLowercase: 0,
    });
  }

  return (
    <CreateAccountForm
      handleRegistration={handleRegistration}
      newUser={newUser}
      setNewUser={setNewUser}
      response={response}
      checkPwdMatch={checkPwdMatch}
      checkPwdLength={checkPwdLength}
      checkPwdSymbol={checkPwdSymbol}
      checkPwdUppercase={checkPwdUppercase}
      checkPwdLowercase={checkPwdLowercase}
      checkPwdNum={checkPwdNum}
      measurePwdLength={measurePwdLength}
      captchaToken={captchaToken}
      setCaptchaToken={setCaptchaToken}
    />
  );
}

export default CreateAccountPage;
